import {
    combineSlices,
    compose,
    configureStore,
    type ConfigureStoreOptions,
    type ThunkAction,
    type UnknownAction,
} from '@reduxjs/toolkit';
import { type TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import persistState from 'redux-sessionstorage';

import socketMiddleware from 'services/socketMiddleware';
import { announcementApi } from 'store/api/announcementApi';
import { apertureApi } from 'store/api/apertureApi';
import { authApi } from 'store/api/authApi';
import { clientApi } from 'store/api/clientApi';
import { clinicalPolicyApi } from 'store/api/clinicalPolicyApi';
import { intakeCaseApi } from 'store/api/intakeCaseApi';
import { notificationApi } from 'store/api/notificationApi';
import { onboardingApi } from 'store/api/onboardingApi';
import { opportunityCuratorApi } from 'store/api/opportunityCuratorApi';
import { payerRequestApi } from 'store/api/payerRequestApi';
import { performanceApi } from 'store/api/performanceApi';
import { referenceApi } from 'store/api/referenceApi';
import { scopeAssessorApi } from 'store/api/scopeAssessorApi';
import { treatmentApi } from 'store/api/treatmentApi';
import { authSlice } from 'store/auth/authSlice';
import { gridsSlice } from 'store/grids/gridsSlice';
import { notificationSlice } from 'store/notification/notificationSlice';
import { referralListSlice } from 'store/referrals/referralListSlice';
import { treatmentListSlice } from 'store/treatments/treatmentListSlice';
import { viewsSlice } from 'store/views/viewsSlice';

const sessionStorageEnhancer = compose(
    persistState([gridsSlice.name, viewsSlice.name], { key: 'apertureStore' }),
);

const slices = [
    authSlice,
    gridsSlice,
    notificationSlice,
    referralListSlice,
    treatmentListSlice,
    viewsSlice,
];

const apiSlices = [
    authApi,
    announcementApi,
    apertureApi,
    clientApi,
    clinicalPolicyApi,
    intakeCaseApi,
    notificationApi,
    onboardingApi,
    opportunityCuratorApi,
    payerRequestApi,
    performanceApi,
    referenceApi,
    scopeAssessorApi,
    treatmentApi,
];

const RESET_STORE_ACTION = 'RESET_STORE';

const baseReducer = combineSlices(...slices, ...apiSlices);
const rootReducer = (
    state: ReturnType<typeof baseReducer> | undefined,
    action: UnknownAction,
): ReturnType<typeof baseReducer> => {
    if (action.type === RESET_STORE_ACTION) {
        return baseReducer({ auth: state?.auth }, action);
    }
    return baseReducer(state, action);
};

export const store = configureStore({
    reducer: rootReducer,
    middleware: (getDefaultMiddleware) =>
        getDefaultMiddleware({ serializableCheck: false })
            .concat(apiSlices.map((slice) => slice.middleware))
            .concat(socketMiddleware),
    enhancers: (getDefaultEnhancers) => getDefaultEnhancers().concat(sessionStorageEnhancer),
    devTools: process.env.REACT_APP_ENV !== 'prd',
});

export type AppDispatch = typeof store.dispatch;
export const useAppDispatch: () => AppDispatch = useDispatch;

export type RootState = ReturnType<typeof store.getState>;
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

export type AppThunk<ReturnType = void> = ThunkAction<
    ReturnType,
    RootState,
    unknown,
    UnknownAction
>;

export const resetStore = () => (dispatch: AppDispatch) => {
    dispatch({ type: RESET_STORE_ACTION });
    apiSlices.forEach((slice) => {
        dispatch(slice.util.resetApiState());
    });
};

/** Used for configuration of redux store in tests */
export const MockStore: ConfigureStoreOptions = {
    reducer: rootReducer,
    middleware: (getDefaultMiddleware) =>
        getDefaultMiddleware({ serializableCheck: false })
            .concat(apiSlices.map((slice) => slice.middleware))
            .concat(socketMiddleware),
};
